import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment/environment';
import { Router } from '@angular/router';
import { AuthHttp } from '../http/http.service';
import { SESSION_TIME_ALIVE_SECONDS } from '@app/constants/session.constants';

@Injectable()
export class AuthenticationService {
  private permisos: any;
  private configuraciones: any;
  private currentUser: any;
  private userName: any;
  private readonly timeAlive: any = SESSION_TIME_ALIVE_SECONDS;
  private readonly time: any;
  public isLoggedIn = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly router: Router,
    private readonly authHttp: AuthHttp
  ) {
    this.time = Date.now() / 1000;
    this.isLoggedIn = !!this.getCurrentUser();

    this.initializePermisos();
    this.inicilizaConfiguraciones();
  }

  private initializePermisos() {
    try {
      const permisosStr = localStorage.getItem('permisos');
      if (permisosStr) {
        const parsedPermisos = JSON.parse(permisosStr);
        // Si los permisos están dentro de una propiedad 'Permiso', los extraemos
        this.permisos = parsedPermisos.Permiso || parsedPermisos;
      }
    } catch (error) {
      console.error('Error initializing permissions:', error);
      this.permisos = {};
    }
  }

  private inicilizaConfiguraciones() {
    try {
      this.configuraciones = JSON.parse(localStorage.getItem('configuraciones'));
    }
    catch (error) {
      console.error('Error initializing configurations:', error);
      this.configuraciones = {};
    }
  }

  login(login: string, password: string, dominio: string) {
    return this.httpClient.post(`${environment.apiURLs.public}/auth/login`, {
      login: login,
      password,
      dominio
    });
  }

  /**
   * Login con DeipeLogin
   * @param login
   * @param password
   * @param dominio
   */
  getDLToken(login: string, password: string, dominio: string) {
    return this.authHttp.post<Record<string, string>>(`${environment.apiURLs.private}/auth/login`,
      {
        "dominio":dominio,
        "login":login,
        "password":password
      }
    );
  }

  /**
   * Login con GW2
   * @param login
   * @param password
   * @param dominio
   */
  getGW2Token(login: string, password: string, dominio: string) {
    return this.authHttp.post<Record<string, string>>(`${environment.apiURLs.private}/auth/gw2/login`,
      {
        "dominio":dominio,
        "login":login,
        "password":password
      }
    );
  }

  getConfigurations() {
    return this.authHttp.get(`${environment.apiURLs.private}/permisos`)
  }

  check() {
    return this.httpClient.get(`${environment.apiURLs.public}/token-is-live`);
  }

  logout() {
    this.clear();
    this.isLoggedIn = false;
    this.router.navigate(['/login']);
  }

  clear() {
    const authKeys = [
      'current_user',
      'access_token',
      'permisos',
      'configuraciones',
      'perfil',
      'dominio',
      'dlToken',
      'gw2Token',
      'ambito_id',
      'selected_language',
      'configuraciones_tienda'
    ];

    authKeys.forEach((key) => localStorage.removeItem(key));

    Object.keys(localStorage).forEach((key) => {
      if (/^(SucursalService)/.test(key) || /^(ArbolService)/.test(key)) {
        localStorage.removeItem(key);
      }
    });

    this.currentUser = null;
    this.userName = null;
    this.permisos = null;
    this.configuraciones = null;
  }

  getCurrentUser() {
    if (this.currentUser === null || this.currentUser === undefined) {
      if (!localStorage.getItem('current_user')) return null;
      this.currentUser = JSON.parse(localStorage.getItem('current_user'));
    }
    return this.currentUser;
  }

  getUserName() {
    try {
      if (this.userName === null) {
        this.userName = this.getCurrentUser().usuario;
      }
      return this.userName;
    } catch (error) {
      return '';
    }
  }

  public tienePermisodeGrupo() {
    if (!this.getCurrentUser()) return false;
    const perfil = this.getCurrentUser().perfil;
    if (!perfil || typeof perfil !== 'string') return false;
    const partesPerfil = perfil.split('-');
    return partesPerfil?.[0] === 'grupo';
  }

  public esUsuarioMultiopticas() {
    const perfil = this.getCurrentUser().perfil;
    if (!perfil || typeof perfil !== 'string') return false;
    const partesPerfil = perfil.split('-');
    return partesPerfil?.[1] && partesPerfil[1] === 'multiopticas';
  }

  isSudo() {
    const currentUser = this.getCurrentUser();
    return (currentUser?.perfil === 'admin') || this.tienePermisodeGrupo();
  }

  can(permiso: string) {
    if (!permiso) return false;
    permiso = permiso.toLowerCase();
    try {
      // Si es superusuario, tiene todos los permisos
      if (this.isSudo()) {
        return true;
      }

      // Si no hay permisos cargados, intentamos cargarlos
      if (!this.permisos) { this.initializePermisos(); }
      if (!this.configuraciones) { this.inicilizaConfiguraciones(); }

      // Obtenemos el valor del permiso
      const value = this.permisos[permiso];

      // Si el valor es undefined, intentamos buscar en configuraciones
      if (value === undefined && this.configuraciones) {
        return this.configuraciones[permiso] === 'S' ||
               this.configuraciones[permiso] === 'T' ||
               this.configuraciones[permiso] === 'M';
      }

      // Verificamos el valor del permiso
      return value === 'S' || value === 'T' || value === 'M';
    } catch (error) {
      console.error(`Error checking permission ${permiso}:`, error);
      return false;
    }
  }

  hasPermission(permission: string, toCompare: string) {
    if (!permission || !toCompare) return false;

    permission = permission.toLowerCase();

    try {
      if (this.isSudo()) return true;

      if (!this.permisos) { this.initializePermisos(); }
      if (!this.configuraciones) { this.inicilizaConfiguraciones(); }

      const value = this.permisos[permission];

      if (value === undefined && this.configuraciones) {
        const configValue = this.configuraciones[permission];
        return toCompare.includes("!") ?
          configValue !== toCompare.replace("!","")
          : configValue === toCompare;
      }

      return toCompare.includes("!") ?
        value !== toCompare.replace("!","")
        : value === toCompare;
    } catch (error) {
      console.error(`Error checking permission ${permission}:`, error);
      return false;
    }
  }

  parseJWT(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  }
}
